/**
 * Purpose: Custom hook for calculating KPI metrics from filtered reservations data.
 * 
 * High-level Workflow:
 * 1. Extracts booked reservations from date-filtered reservations (excludes inquiry/cancelled)
 * 2. Calculates inquiries count from date-filtered reservations (all statuses)
 * 3. Calculates cancellations count from date-filtered reservations (all statuses)
 * 4. Calculates period nights from booked reservations only (sum of nights)
 * 5. Calculates period gross revenue from booked reservations only (sum of revenue)
 * 6. Extracts unique channels from displayed reservations (for table filtering)
 * 7. Builds KPI cards array with formatted values (same order, labels, formatting as original)
 * 
 * Assumptions & Invariants:
 * - dateFilteredReservations contains all statuses (booked, inquiry, cancelled, owner_stay)
 * - displayedReservations excludes inquiry and cancelled (only booked and owner_stay)
 * - KPIs use booked reservations only for nights and revenue calculations
 * - KPIs use all statuses for counts (inquiries, cancellations)
 * - Reservations count KPI uses displayedReservations.length (excludes inquiry/cancelled)
 * - All memoization boundaries preserve exact dependency semantics from original implementation
 * 
 * Dependencies:
 * Internal:
 *   - utils/hostawayStatus.js - getBookedReservations utility
 * External:
 *   - react - React hooks (useMemo)
 * 
 * Version: 1.0.0
 * Created: 2026-01-26
 * Change Log:
 * - 2026-01-26: Initial extraction from ReservationsPage.jsx (Phase 4)
 */

import { useMemo } from 'react';
import { getBookedReservations } from '../utils/hostawayStatus.js';

/**
 * Custom hook for calculating KPI metrics
 * 
 * Calculates KPI primitives (counts, sums) and builds KPI cards array.
 * Preserves exact memoization boundaries and dependencies from original implementation.
 * 
 * @param {Array} dateFilteredReservations - Reservations filtered by date overlap (all statuses)
 * @param {Array} displayedReservations - Reservations filtered for table display (excludes inquiry/cancelled)
 * @param {Object|null} aggregates - Optional backend aggregates for score cards (reservations_count, total_nights, total_base_rate, average_price_per_night, total_owner_net, cancellations_count, date_range_start, date_range_end)
 * @param {string} dateRangeLabel - Formatted date range for tooltips (e.g. "Jan 1, 2026 – Feb 1, 2026")
 * @param {boolean} debug - Debug flag for logging (optional, defaults to false)
 * @returns {Object} - { bookedReservations, inquiriesCount, cancellationsCount, periodNights, periodGross, channels, kpiCards }
 */
export function useReservationsKPIs(dateFilteredReservations, displayedReservations, aggregates = null, dateRangeLabel = '', debug = false) {
  // Calculate KPIs from dateFilteredReservations (all statuses for counts, booked only for revenue/nights)
  const bookedReservations = useMemo(() => {
    return getBookedReservations(dateFilteredReservations);
  }, [dateFilteredReservations]);
  
  // Calculate inquiries count (for KPI)
  const inquiriesCount = useMemo(() => {
    return dateFilteredReservations.filter(r => r.is_inquiry).length;
  }, [dateFilteredReservations]);
  
  // Calculate cancellations count (for KPI)
  const cancellationsCount = useMemo(() => {
    return dateFilteredReservations.filter(r => r.is_cancelled).length;
  }, [dateFilteredReservations]);
  
  // Calculate summary metrics from BOOKED reservations only (for Nights and Revenue KPIs)
  const periodNights = useMemo(() => {
    const sum = bookedReservations.reduce((acc, r) => {
      const nights = Number(r.nights) || 0;
      return acc + nights;
    }, 0);
    
    if (debug) {
      const nightsArray = bookedReservations.map(r => Number(r.nights) || 0);
      console.log('[useReservationsKPIs] Nights KPI calculation (booked only):', {
        nightsArray,
        sum,
        bookedReservationsCount: bookedReservations.length,
        displayedReservationsCount: displayedReservations.length,
        note: 'KPIs use booked reservations only for Nights and Revenue',
      });
    }
    
    return sum;
  }, [bookedReservations, displayedReservations.length, debug]);
  
  const periodGross = useMemo(() => {
    return bookedReservations.reduce((acc, r) => {
      const revenue = Number(r.revenue) || 0;
      return acc + revenue;
    }, 0);
  }, [bookedReservations]);

  const fallbackTotalBaseRate = useMemo(() => {
    return displayedReservations.reduce((acc, r) => acc + (parseFloat(r.base_rate) || 0), 0);
  }, [displayedReservations]);
  const fallbackTotalOwnerNet = useMemo(() => {
    return displayedReservations.reduce((acc, r) => {
      const ownerNet = r.financials?.ownerNet != null ? Number(r.financials.ownerNet) : null;
      return acc + (ownerNet != null && !isNaN(ownerNet) ? ownerNet : 0);
    }, 0);
  }, [displayedReservations]);

  // Phase 1.5: Memoized to prevent unnecessary array creation
  const channels = useMemo(() => {
    return [...new Set(displayedReservations.map(r => r.channel).filter(Boolean))];
  }, [displayedReservations]);

  const currencyFmt = { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 };
  const currencyFmt2 = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };

  // Score cards: Reservations, Nights, Average Price/Night, Owner Net Revenue, Cancellations (Inquiries removed)
  const kpiCards = useMemo(() => {
    const useAgg = aggregates != null;
    const reservationsCount = useAgg ? (aggregates.reservations_count ?? 0) : displayedReservations.length;
    const nights = useAgg ? (aggregates.total_nights ?? 0) : periodNights;
    const totalBaseRate = useAgg ? (aggregates.total_base_rate ?? 0) : fallbackTotalBaseRate;
    // Average Price/Night: backend-only; no client-side computation. Show "—" if aggregates missing or average_price_per_night null.
    const avgPricePerNightFromBackend = useAgg ? (aggregates.average_price_per_night ?? null) : null;
    const totalOwnerNet = useAgg ? (aggregates.total_owner_net ?? 0) : fallbackTotalOwnerNet;
    const cancCount = useAgg ? (aggregates.cancellations_count ?? 0) : cancellationsCount;
    const rangeLabel = dateRangeLabel || (aggregates?.date_range_start && aggregates?.date_range_end ? `${aggregates.date_range_start} – ${aggregates.date_range_end}` : '');

    return [
      { label: 'Reservations', value: String(reservationsCount), tooltip: rangeLabel ? `Count of reservations (excluding inquiries and cancelled) for ${rangeLabel}.` : undefined, 'data-testid': 'kpi-reservations-count' },
      { label: 'Nights', value: String(nights), tooltip: rangeLabel ? `Total nights for ${rangeLabel}.` : undefined, 'data-testid': 'kpi-booked-nights' },
      { label: 'Average Price/Night', value: avgPricePerNightFromBackend != null ? new Intl.NumberFormat('en-US', currencyFmt2).format(avgPricePerNightFromBackend) : '—', tooltip: 'Base Rate only (excludes cleaning, taxes, and fees).', 'data-testid': 'kpi-average-price-per-night' },
      { label: 'Owner Net Revenue', value: new Intl.NumberFormat('en-US', currencyFmt).format(totalOwnerNet), tooltip: rangeLabel ? `Net revenue after deductions for ${rangeLabel}.` : 'Net revenue after deductions.', 'data-testid': 'kpi-owner-net-revenue' },
      { label: 'Cancellations', value: String(cancCount), tooltip: rangeLabel ? `Cancelled reservations in ${rangeLabel}.` : undefined, 'data-testid': 'kpi-cancellations-count' },
    ];
  }, [aggregates, dateRangeLabel, displayedReservations.length, periodNights, fallbackTotalBaseRate, fallbackTotalOwnerNet, cancellationsCount]);

  return {
    bookedReservations,
    inquiriesCount,
    cancellationsCount,
    periodNights,
    periodGross,
    channels,
    kpiCards,
  };
}
